import Diag = require("VSS/Diag");
import Utils_Core = require("VSS/Utils/Core");
import Utils_Array = require("VSS/Utils/Array");
import * as VSS_Service from "VSS/Service";

import CSVRowViewModel = require("TestManagement/Scripts/TFS.TestManagement.CSVRowViewModel");
import { TestManagementRegistrySettingsService } from "TestManagement/Scripts/TestManagementRegistrySettingsSerivce";
import TestsOM = require("TestManagement/Scripts/TFS.TestManagement");
import TMUtils = require("TestManagement/Scripts/TFS.TestManagement.Utils");
import WITOM = require("WorkItemTracking/Scripts/TFS.WorkItemTracking");
import { WorkItemManager } from "WorkItemTracking/Scripts/OM/WorkItemManager";
import TCMConstants = require("Presentation/Scripts/TFS/Generated/TFS.TestManagement.Constants");
import Resources = require("TestManagement/Scripts/Resources/TFS.Resources.TestManagement");

let WITUtils = TMUtils.WorkItemUtils;
let TestCaseCategoryUtils = TMUtils.TestCaseCategoryUtils;

enum CsvFixedColumn {
    ID = 0,
    WorkItemType,
    Title,
    TestStep,
    StepAction,
    StepExpected,
    EnumLength
}
export class TestCaseImportHelperConstants {
    public static ProposedState = "Design";
}

/**
 * TestCaseImportHelper: This is the helper class, which create/update testcases from the imported row level contents from CSV.
 */
export class TestCaseImportHelper {
    private _registrySettingsService: TestManagementRegistrySettingsService;
    private _workItemType: WITOM.WorkItemType;
    private _plan: any;
    private _suite: TestsOM.ITestSuiteModel;
    private _batchSizeToRecordsIndexMap: number[] = [];
    private _sharedStepIdToWorkItemMap: { [id: number]: WITOM.WorkItem } = {};
    private _sharedStepsIdToSharedStepMap: { [id: number]: TestsOM.SharedSteps } = {};
    private _testCasesUpdated: number = 0;
    private _testCasesCreated: number = 0;
    private _testCaseProposedStateName: string = "Design";
    private _fileType: string;

    private static readonly BATCH_SIZE_KEY="/Service/TestManagement/Settings/TestCasesExportImportBatchSize";
    private _workItemBatchSize = 200;

    constructor() {
        this._workItemType = null;
    }

    public initialize(plan?: any, suite?: TestsOM.ITestSuiteModel, fileType?: string, callback?: IResultCallback) {
        this._setBatchSize();
        this._plan = plan;
        this._suite = suite;
        this._fileType = fileType ? fileType : '';

        if (this._workItemType === null) {
            TestCaseCategoryUtils.getDefaultWorkItemTypeInfoForTestCaseCategory((workItemType) => {
                this._workItemType = workItemType;
                this._beginGetFields();
                WITUtils.getWorkItemStore().beginGetLinkTypes(() => {
                    if (callback) {
                        callback(true);
                    }
                });
                // Getting State Name for Proposed State Category (Design) of test case, this is in the locale of Collection language and needs to be fetched from wit.
                // We have a check based on this while import to ensure newly added test cases are in Design state.
                TestCaseCategoryUtils.getDefaultProposedStateForTestCaseCategory(workItemType.name, (workItemStates) => {
                    for(var state of workItemStates)
                    {
                        if(state.Category === TestCaseImportHelperConstants.ProposedState){
                            this._testCaseProposedStateName = state.Name;
                        }
                    }
                });
            });
        }
    }

    public convertCSVToTestCasesData(contents: string[][], callback: IResultCallback, errorCallback: IErrorCallback) {
        this._getCSVImportData(contents, (sharedStepIds, records) => {
            if (sharedStepIds && sharedStepIds.length > 0) {
                let allSharedStepWorkItems: WITOM.WorkItem[] = [];
                const end = sharedStepIds.length < this._workItemBatchSize ? sharedStepIds.length : this._workItemBatchSize;
                //Fetch shared steps data using sharedStepIds
                this._beginFetchSharedSteps(allSharedStepWorkItems, sharedStepIds, 0, end, (sharedStepWorkItems: WITOM.WorkItem[]) => {
                    this._populateSharedStepsIdToWorkItemMap(sharedStepWorkItems);
                    this._saveAllTestCase(records, 0, this._batchSizeToRecordsIndexMap[0], 1, callback, errorCallback);
                },
                    errorCallback);
            }
            else {
                this._saveAllTestCase(records, 0, this._batchSizeToRecordsIndexMap[0], 1, callback, errorCallback);
            }
        });
    }

    private _setBatchSize() {
        this._registrySettingsService = VSS_Service.getService(TestManagementRegistrySettingsService);
        this._registrySettingsService.getValue(TestCaseImportHelper.BATCH_SIZE_KEY,
            this._workItemBatchSize.toString()).then((batchSizeStr: string) => {
                this._workItemBatchSize = Number(batchSizeStr);
        }).catch((error) => {
            Diag.logError("[_setBatchSize] " + error);
        });
    }

    //Fetch shared steps data in batches using sharedStepIds
    private _beginFetchSharedSteps(allSharedStepWorkItems: WITOM.WorkItem[], sharedStepIds: number[], start: number, end: number, onBatchingComplete: IResultCallback, errorCallback: IErrorCallback) {
        this._beginFetchSharedStepsInBatch(sharedStepIds.slice(start, end), (sharedStepWorkItems) => {
            allSharedStepWorkItems = allSharedStepWorkItems.concat(sharedStepWorkItems);
            if (end === sharedStepIds.length) { 
                onBatchingComplete(allSharedStepWorkItems);
            }
            else {
                start = end;
                end = (end + this._workItemBatchSize) < sharedStepIds.length ?
                    (end + this._workItemBatchSize) : sharedStepIds.length;
                this._beginFetchSharedSteps(allSharedStepWorkItems, sharedStepIds, start, end, onBatchingComplete, errorCallback);
            }
        },
            errorCallback);
    }

    private _beginFetchSharedStepsInBatch(sharedStepIds: number[], callback: IResultCallback, errorCallback: IErrorCallback) {
        TMUtils.WorkItemUtils.getWorkItemStore().beginGetWorkItems(
            sharedStepIds,
            (sharedStepWorkItems: WITOM.WorkItem[]) => {
                if (callback) {
                    callback(sharedStepWorkItems);
                }
            },
            errorCallback);
    }

    private _saveAllTestCase(
        records: CSVRowViewModel.CSVRowViewModel[],
        start: number,
        end: number,
        batchNumber: number,
        callback: IResultCallback,
        errorCallback: IErrorCallback
    ): void {
        this._saveAllTestCasesInBatch(
            records.slice(start, end + 1),
            (result) => {
                if (batchNumber < this._batchSizeToRecordsIndexMap.length) {
                    if (result) {
                        callback(this._getPercentComplete(batchNumber), this._testCasesUpdated, this._testCasesCreated);
                    }
                    batchNumber++;
                    this._saveAllTestCase(records, end + 1, this._batchSizeToRecordsIndexMap[batchNumber - 1], batchNumber, callback, errorCallback);
                }
                else {
                    //Save shared steps after saving testcases
                    let sharedSteps: TestsOM.SharedSteps[] = this._getSharedSteps();
                    if (sharedSteps && sharedSteps.length > 0) {
                        this._saveAllSharedSteps(sharedSteps, 0, this._workItemBatchSize, callback, errorCallback);

                    }
                    else {
                        //if shared steps are empty, then return callback to the client
                        if (result) {
                            callback(100, this._testCasesUpdated, this._testCasesCreated);
                        }
                    }
                }
            },
            (error) => {
                Diag.logError("[_saveAllTestCase] " + error);
                // in case of an error we want to skip to the next batch , so returning a callback here 
                callback(this._getPercentComplete(batchNumber), this._testCasesUpdated, this._testCasesCreated, error);

                batchNumber++;
                this._saveAllTestCase(records, end + 1, this._batchSizeToRecordsIndexMap[batchNumber - 1], batchNumber, callback, errorCallback);
            }
        );
    }

    private _getPercentComplete(batchNumber: number): number {
        let percentComplete = (this._batchSizeToRecordsIndexMap[batchNumber - 1] * 100) /
            this._batchSizeToRecordsIndexMap[this._batchSizeToRecordsIndexMap.length - 1];

        return isNaN(percentComplete) ? 100: Math.round(percentComplete);
    }

    //This method converts CSV contents into row level records
    private _getCSVImportData(contents: string[][], callback: IResultCallback) {
        let records: CSVRowViewModel.CSVRowViewModel[] = [],
            columns: string[] = contents[0].slice(),
            columnLength = columns.length,
            contentLength = contents.length,
            fixedColumnLength: number = CsvFixedColumn.EnumLength,
            id: number,
            workItemType: string,
            testCaseTitle: string,
            isTestCase: boolean,
            isOldTestCase: boolean,
            oldTestCases: TestsOM.TestCase[] = [],
            sharedStepIds: number[] = [],
            isSharedStep: boolean = false,
            property: string,
            tempTestCase: TestsOM.TestCase,
            action: string,
            expectedResult: string,
            additionalFields: any = {},
            actionId: number,
            additionalFieldProperties: string[] = [],
            testCaseCountInCurrentBatch = 0,
            batchIndex = 0,
            linesIgnored = 0

        for (let columnIndex = fixedColumnLength; columnIndex < columnLength; columnIndex++) {
            additionalFieldProperties.push(this._getTestcasePropertyName(columns[columnIndex]));
        }

        for (let rowIndex = 1; rowIndex < contentLength; rowIndex++) {
            let rowData = contents[rowIndex];
            id = rowData[CsvFixedColumn.ID] ? parseInt(rowData[CsvFixedColumn.ID], 10) : 0;
            workItemType = rowData[CsvFixedColumn.WorkItemType];
            testCaseTitle = rowData[CsvFixedColumn.Title];
            isTestCase = false;
            isOldTestCase = false;
            tempTestCase = null;
            isSharedStep = false;
            additionalFields = {};

            if (workItemType.toLowerCase() === Resources.TestCaseText.toLowerCase()) {
                isTestCase = true;
            }
            else if (workItemType.toLowerCase() === Resources.NewSharedStep.toLowerCase()) {
                isSharedStep = true;
            }

            if (isTestCase) {
                //Create test case record
                if (
                    rowData[CsvFixedColumn.TestStep] !== "" ||
                    rowData[CsvFixedColumn.StepAction] !== "" ||
                    rowData[CsvFixedColumn.StepExpected] !== ""
                ) {
                    Diag.logError("[_getCSVImportData] Test Steps must start from next row of the Test Case at line " + (rowIndex + 1));
                }

                if (testCaseCountInCurrentBatch === this._workItemBatchSize) {
                    testCaseCountInCurrentBatch = 0;
                    batchIndex++;
                }
                testCaseCountInCurrentBatch++;
                for (let columnIndex = fixedColumnLength; columnIndex < columnLength; columnIndex++) {
                    property = additionalFieldProperties[columnIndex - fixedColumnLength];
                    if (property !== "") {
                        additionalFields[property] = rowData[columnIndex];
                    }
                }

                if (id > 0) {
                    isOldTestCase = true;
                    tempTestCase = new TestsOM.TestCase(id, 0, testCaseTitle, [], [], null, "", "", additionalFields);
                    oldTestCases.push(tempTestCase);
                }

                records.push(new CSVRowViewModel.CSVRowViewModel(testCaseTitle, 0, "", "", tempTestCase, isOldTestCase, false, 0, additionalFields));
            } else {
                action = rowData[CsvFixedColumn.StepAction];
                expectedResult = rowData[CsvFixedColumn.StepExpected];
                actionId = parseFloat(rowData[CsvFixedColumn.TestStep]);
                const newRowIndex = this._skipOrphanTestStepRows(records, contents, rowIndex, actionId);

                if (newRowIndex !== rowIndex) {
                    // orphan test step rows exist ignore the rows
                    linesIgnored += newRowIndex - rowIndex;
                    rowIndex = newRowIndex - 1;
                }
                else {
                    if (isSharedStep) {
                        //Create shared step record
                        records.push(new CSVRowViewModel.CSVRowViewModel("", actionId, action, expectedResult, null, false, true, id));
                        sharedStepIds.push(id);
                    }
                    else {
                        //Create normal step record
                        records.push(new CSVRowViewModel.CSVRowViewModel("", actionId, action, expectedResult, null, false));
                    }
                }
            }

            this._batchSizeToRecordsIndexMap[batchIndex] = rowIndex - linesIgnored - 1;
        }

        this._beginGetWorkItemsForTestCasesIfNeeded(oldTestCases, () => {
            if (callback) {
                callback(Utils_Array.unique(sharedStepIds), records);
            }
        });
    }

    private async _beginGetFields() {
        await WITUtils.getWorkItemStore().beginGetFields();
    }

    private _getTestcasePropertyName(columnName: string): string {
        let propertyName: string = "";

        let fieldDefinition = WITUtils.getWorkItemStore().getFieldDefinition(columnName);

        if (fieldDefinition) {
            propertyName = fieldDefinition.referenceName;
        }

        return propertyName;
    }

    // Initiates bulk save for the changes made.
    private _saveAllTestCasesInBatch(records: CSVRowViewModel.CSVRowViewModel[], callback: IResultCallback, error: IErrorCallback): void {
        // Create test cases from the records
        let index = 0,
            length = records.length,
            record: CSVRowViewModel.CSVRowViewModel,
            currentTestCase: TestsOM.TestCase,
            anyValidStepFound: boolean = false,
            stepRecords: CSVRowViewModel.CSVRowViewModel[] = [],
            testCasesBeingPreparedForEdit = 0,
            testCaseRowIndexes: number[] = [],
            testCases: TestsOM.TestCase[] = [];

        for (index = 0; index < length; index++) {
            record = records[index];
            if ((record.testCaseTitle || "").trim()) {
                if (currentTestCase) {
                    if (anyValidStepFound) {
                        testCasesBeingPreparedForEdit++;
                        // Add all the steps found till now to the current testcase
                        this._addTestSteps(currentTestCase, stepRecords, () => {
                            testCasesBeingPreparedForEdit--;
                        });
                    }
                }

                if (record.testCase) {
                    // This means it is already a saved test case.
                    currentTestCase = record.testCase;
                }
                else {
                    this._validateTestCaseFields(record);
                    // Create a new test case.
                    currentTestCase = this._createTestCase(record);
                    record.testCase = currentTestCase;
                }

                testCases.push(currentTestCase);
                testCaseRowIndexes.push(index);
                anyValidStepFound = false;
                stepRecords = [];
            }
            else {
                if (!record.testCase) {
                    anyValidStepFound = true;
                    stepRecords.push(record);
                }
            }
        }

        // All records processed
        if (testCasesBeingPreparedForEdit === 0) {
            if (currentTestCase) {
                // Add all the steps found till now to the current testcase
                this._addTestSteps(currentTestCase, stepRecords, () => {
                    this._beginSaveTestCases(records, testCases, testCaseRowIndexes, callback, error);
                });
            }
        }
    }

    private _beginSaveTestCases(
        records: CSVRowViewModel.CSVRowViewModel[],
        testCases: TestsOM.TestCase[],
        testCaseRowIndexes: number[],
        callback: IResultCallback,
        error: IErrorCallback
    ) {
        let i = 0,
            l = testCases.length,
            testCase: TestsOM.TestCase,
            workItem: WITOM.WorkItem,
            newWorkItems: WITOM.WorkItem[] = [],
            newWorkItemIds: number[] = [],
            validWorkItems: WITOM.WorkItem[] = [];

        //first get the testcase workitems for testcases which are dirty and the corresponding workitems havent been fetched
        for (i = 0; i < l; i++) {
            testCase = testCases[i];
            if (testCase.getWorkItemWrapper()) {
                //Set parameter info for testcases
                this._setParameterInfoForTestCase(testCase);

                this._updateTestCaseWorkItemFieldsFromRecord(testCase, records[testCaseRowIndexes[i]]);
                testCase.preSave();
                workItem = testCase.getWorkItemWrapper().getWorkItem();

                if (workItem.isValid()) {
                    validWorkItems.push(workItem);
                    if (workItem.isNew()) {
                        newWorkItems.push(workItem);
                    }
                }
                else {
                    let errorStr = "Invalid workitem field values found";
                    Diag.logError("[_beginSaveTestCases] " + errorStr)
                    error(errorStr);
                }
            }
        }

        //Save valid workitems batch
        if (validWorkItems.length > 0) {
            WITUtils.getWorkItemStore().beginSaveWorkItemsBatch(validWorkItems, () => {
                for (i = 0, l = newWorkItems.length; i < l; i++) {
                    newWorkItemIds.push(newWorkItems[i].id);
                }

                this._testCasesCreated = this._testCasesCreated + newWorkItemIds.length;
                this._testCasesUpdated = this._testCasesUpdated + (validWorkItems.length - newWorkItemIds.length);

                //Add new testcases to the suite
                if (newWorkItemIds.length > 0) {
                    TMUtils.TestSuiteUtils.beginAddTestCasesToSuite(
                        newWorkItemIds,
                        this._plan.id,
                        this._suite,
                        (suiteUpdate: TestsOM.ITestSuiteModel) => {
                            if (suiteUpdate) {
                                this._finalizeSave(callback, true);
                            }
                        },
                        error
                    );
                } else {
                    this._finalizeSave(callback, true);
                }
            }, (errorMessage) => {
                Diag.logError("[beginSaveWorkItemsBatch] " + errorMessage)
                error(errorMessage);
            });
        }
        else {
            this._finalizeSave(callback, true);
        }
    }

    private _saveAllSharedSteps(
        sharedSteps: TestsOM.SharedSteps[],
        start: number,
        end: number,
        callback: IResultCallback,
        errorCallback: IErrorCallback
    ) {
        this._saveAllSharedStepsInBatch(
            sharedSteps.slice(start, end),
            (result) => {
                if (end < sharedSteps.length) {
                    this._saveAllSharedSteps(sharedSteps, end, end + this._workItemBatchSize, callback, errorCallback);
                }
                else {
                    //Send final response to the client after updating shared steps
                    if (result) {
                        callback(100, this._testCasesUpdated, this._testCasesCreated);
                    }
                }
            },
            (error) => {
                Diag.logError("[_saveAllSharedSteps] " + error);
                errorCallback(error);
            }
        );

    }
    private _saveAllSharedStepsInBatch(
        sharedSteps: TestsOM.SharedSteps[],
        callback: IResultCallback,
        error: IErrorCallback
    ) {
        let i = 0,
            l = sharedSteps.length,
            sharedStep: TestsOM.SharedSteps,
            workItem: WITOM.WorkItem,
            validWorkItems: WITOM.WorkItem[] = [];

        for (i = 0; i < l; i++) {
            sharedStep = sharedSteps[i];
            sharedStep.getSharedStep().preSave();
            workItem = sharedStep.getSharedStep().getWorkItemWrapper().getWorkItem();

            if (workItem.isValid()) {
                validWorkItems.push(workItem);
            }
            else {
                let errorStr = "Invalid workitem field values found";
                Diag.logError("[_beginSaveSharedSteps] " + errorStr)
                error(errorStr);
            }
        }

        //Save valid shared steps
        if (validWorkItems.length > 0) {
            WITUtils.getWorkItemStore().beginSaveWorkItemsBatch(validWorkItems, () => {
                this._finalizeSave(callback, true);
            }, (errorMessage) => {
                Diag.logError("[_beginSaveSharedSteps] " + errorMessage)
                error(errorMessage);
            });
        }
    }

    private _setParameterInfoForTestCase(testCase: TestsOM.TestCase): void {
        let workItem: WITOM.WorkItem = testCase.getWorkItemWrapper().getWorkItem(),
            parametersXmlFieldId = workItem.getField(TCMConstants.WorkItemFieldNames.Parameters).fieldDefinition.id,
            parametersDataXmlFieldId = workItem.getField(TCMConstants.WorkItemFieldNames.DataField).fieldDefinition.id,
            parametersXml = workItem.getFieldValue(parametersXmlFieldId),
            parametersDataFieldValue = workItem.getFieldValue(parametersDataXmlFieldId),
            parameters = TestsOM.ParameterCommonUtils.parseParameters($(Utils_Core.parseXml(parametersXml || ""))),
            parameterDataInfo: TestsOM.TestCaseParameterDataInfo = TestsOM.TestCaseParameterDataInfo.parseTestCaseParametersData(parametersDataFieldValue),
            parametersData: Array<{ [index: string]: string; }> = [];

        if (!parameterDataInfo) {
            parametersData = TMUtils.ParametersHelper.parseParametersData($(Utils_Core.parseXml(parametersDataFieldValue || "")), parameters);
        }

        testCase.initializeParameterInfo(parameters, parametersData, parameterDataInfo, parametersDataFieldValue);

        this._readAndMergeSharedStepParameters(testCase);
    }

    private _readAndMergeSharedStepParameters(testCase: TestsOM.TestCase) {
        let steps = testCase.getTestSteps(),
            length = steps.length,
            sharedSteps: TestsOM.SharedSteps,
            workItem: WITOM.WorkItem,
            parametersXmlFieldId: number,
            parametersXml: string,
            parameters: string[],
            $parameterData: JQuery,
            i: number;

        testCase.setSharedStepParameters([]);

        //now read parameters for each shared test step included in this test case
        for (i = 0; i < length; i++) {
            if (steps[i] instanceof TestsOM.SharedSteps) {
                sharedSteps = <TestsOM.SharedSteps>steps[i];
                workItem = this._sharedStepIdToWorkItemMap[sharedSteps.ref];
                parametersXmlFieldId = workItem.getField(TCMConstants.WorkItemFieldNames.Parameters).fieldDefinition.id;
                parametersXml = workItem.getFieldValue(parametersXmlFieldId);
                parameters = TestsOM.ParameterCommonUtils.parseParameters($(Utils_Core.parseXml(parametersXml || "")));
                testCase.mergeSharedStepParameters(parameters);
            }
        }

        // now read parameters data
        if (!testCase.isUsingSharedParameters()) {
            $parameterData = $(Utils_Core.parseXml(testCase.getWorkItemWrapper().getWorkItem().getFieldValue(TCMConstants.WorkItemFieldNames.DataField)));
            TMUtils.ParametersHelper.setTestCaseParametersDataFromXml(testCase, $parameterData, false);
        }
    }

    private _beginGetWorkItemsForTestCasesIfNeeded(testCases: TestsOM.TestCase[], callback: IResultCallback): void {
        let l = testCases.length,
            testCase: TestsOM.TestCase,
            i: number,
            testCaseId: number,
            testCaseIds: number[] = [],
            workItem: WITOM.WorkItem,
            testCasesToFetch: TestsOM.TestCase[] = [],
            idToWorkItemMap = {};
        for (i = 0; i < l; i++) {
            testCase = testCases[i];
            if (!testCase.getWorkItemWrapper()) {
                testCaseId = testCase.getId();
                if (testCaseId) {
                    testCaseIds.push(testCase.getId());
                    testCasesToFetch.push(testCase);
                }
            }
        }

        if (testCaseIds && testCaseIds.length > 0) {
            TMUtils.WorkItemUtils.getWorkItemStore().beginGetWorkItems(
                testCaseIds,
                (workItems) => {
                    if (workItems) {
                        l = workItems.length;
                        for (i = 0; i < l; i++) {
                            idToWorkItemMap[workItems[i].id] = workItems[i];
                        }
                        l = testCasesToFetch.length;
                        for (i = 0; i < l; i++) {
                            testCase = testCasesToFetch[i];
                            if (idToWorkItemMap[testCase.getId()]) {
                                workItem = idToWorkItemMap[testCase.getId()];
                                testCase.setWorkItemWrapper(new TestsOM.WorkItemWrapper(workItem), true);
                            }
                        }
                        if (callback) {
                            callback();
                        }
                    }
                },
                (error) => {
                    Diag.logError(error.message);
                    if (callback) {
                        callback();
                    }
                }
            );
        } else {
            callback();
        }
    }

    private _populateSharedStepsIdToWorkItemMap(workItems: WITOM.WorkItem[]): void {
        let i: number,
            len: number;

        for (i = 0, len = workItems.length; i < len; i++) {
            this._sharedStepIdToWorkItemMap[workItems[i].getUniqueId()] = workItems[i];
        }
    }
    private _getSharedSteps(): TestsOM.SharedSteps[] {
        let sharedSteps: TestsOM.SharedSteps[] = [], sharedStepId: number;
        let sharedStepIdKeys = Object.keys(this._sharedStepsIdToSharedStepMap);

        sharedStepIdKeys.forEach(sharedStepIdKey => {
            sharedStepId = parseInt(sharedStepIdKey, 10);
            if (this._sharedStepsIdToSharedStepMap[sharedStepId]) {
                sharedSteps.push(this._sharedStepsIdToSharedStepMap[sharedStepId]);
            }
        });

        return sharedSteps;
    }

    private _updateTestCaseWorkItemFieldsFromRecord(testCase: TestsOM.TestCase, record: CSVRowViewModel.CSVRowViewModel) {
        let workItemWrapper = testCase.getWorkItemWrapper(),
            property;
        for (property in record.additionalFields) {
            if (record.additionalFields[property] && TestsOM.FieldUtils.isFieldEditable(this._workItemType.getFieldDefinition(property))) {
                workItemWrapper.setFieldValue(property, record.additionalFields[property]);
            }
        }
        workItemWrapper.setTitle(record.testCaseTitle);
    }

    private _addTestSteps(testCase: TestsOM.TestCase, records: CSVRowViewModel.CSVRowViewModel[], callback?: IResultCallback) {
        let index = 0,
            length = records.length,
            lastNonBlankStepIndex = length - 1,
            lastActionId: number = 0,
            record: CSVRowViewModel.CSVRowViewModel,
            sharedStep: TestsOM.SharedSteps = null;

        for (index = 0; index <= lastNonBlankStepIndex; index++) {
            record = records[index];

            if (!record.isSharedStep) {
                lastActionId = testCase.getLastActionId()
                lastActionId++;
                this._addTestStep(testCase, sharedStep, record, lastActionId);
            }
            else {
                //We are considering last occurrence of shared step as final shared step
                sharedStep = this._addSharedTestStep(testCase, record);
                this._sharedStepsIdToSharedStepMap[record.refId] = sharedStep;
            }

            //since these are steps records we no longer need any other field value
            this._clearAdditionalFieldsFromRecord(record);
            record.testCase = testCase;
        }

        if (callback) {
            callback();
        }
    }

    private _finalizeSave(callback: IResultCallback, result: boolean) {
        if (callback) {
            callback(result);
        }
    }

    private _clearAdditionalFieldsFromRecord(record: CSVRowViewModel.CSVRowViewModel) {
        record.additionalFields = {};
    }

    private _addTestStep(testCase: TestsOM.TestCase, sharedStep: TestsOM.SharedSteps, record: CSVRowViewModel.CSVRowViewModel, lastActionId: number) {
        let testStep: TestsOM.TestStep,
            sharedStepLastActionId: number;
        
        if (this._fileType === "csv") {
            // This is required to enable proper parsing of left angular bracket in test steps
            record.action = TestsOM.HtmlUtils.encodeLeftAngularBracket(record.action);
            record.expectedResult = TestsOM.HtmlUtils.encodeLeftAngularBracket(record.expectedResult);
        }
        
        // If the test steps is float eg-2.1, 3.1. It means this step is part of shared step
        if (typeof record.actionId === 'number' && (record.actionId === Math.floor(record.actionId))) {
            testStep = TestsOM.TestStep.createStep(lastActionId, this._getStepType(record), record.action, record.expectedResult, true);
            testCase.addStep(testStep);
        }
        else {
            //If sharedStep is null, then don't add step in shared step
            if (sharedStep) {
                sharedStepLastActionId = sharedStep.getTestSteps().length;
                sharedStepLastActionId++;
                testStep = new TestsOM.TestStep(sharedStepLastActionId, this._getStepType(record), record.action, record.expectedResult, true);
                sharedStep.getSharedStep().addStep(testStep);
            }
        }
    }

    private _addSharedTestStep(testCase: TestsOM.TestCase, record: CSVRowViewModel.CSVRowViewModel): TestsOM.SharedSteps {
        let sharedStepIndex: number,
            sharedStep: TestsOM.SharedSteps;

        sharedStepIndex = testCase.insertSharedStepsAtIndex(testCase.getTestSteps().length, [record.refId]);
        sharedStep = <TestsOM.SharedSteps>testCase.getTestSteps()[sharedStepIndex - 1];

        let sharedStepWorkItem: TestsOM.SharedStepWorkItem = new TestsOM.SharedStepWorkItem(record.refId, 0, record.action, [], []);
        if (sharedStepWorkItem && !sharedStepWorkItem.getWorkItemWrapper()) {
            sharedStepWorkItem.setWorkItemWrapper(new TestsOM.WorkItemWrapper(this._sharedStepIdToWorkItemMap[record.refId]));
        }

        sharedStep.setSharedStepWorkItem(sharedStepWorkItem);

        return sharedStep;
    }

    private _getStepType(record): string {
        if (TestsOM.HtmlUtils.isEmptyText(record.expectedResult)) {
            return TestsOM.TestStepTypes.Action;
        }
        else {
            return TestsOM.TestStepTypes.Validate;
        }
    }

    private _createTestCase(record: CSVRowViewModel.CSVRowViewModel): TestsOM.TestCase {
        let testCase: TestsOM.TestCase;
        let witStore = WITUtils.getWorkItemStore();
        let workItem = WorkItemManager.get(witStore).createWorkItem(this._workItemType);

        WITUtils.setAreaAndIterationPaths(workItem, this._plan.areaPath, this._plan.iterationPath);
        // Create test case with the work item.
        testCase = new TestsOM.TestCase(0, 0, record.testCaseTitle, [], [], null, "", this._plan.areaPath, record.additionalFields);
        testCase.setWorkItemWrapper(new TestsOM.WorkItemWrapper(workItem));
        this._updateTestCaseWorkItemFieldsFromRecord(testCase, record);
        record.isTestCase = true;
        return testCase;
    }

    private _validateTestCaseFields(record: CSVRowViewModel.CSVRowViewModel) {
        if (record.additionalFields["System.State"] && record.additionalFields["System.State"] != this._testCaseProposedStateName) {
            Diag.logError("[validateFields]State field in a new test case should be Design");
        }
    }

    private _skipOrphanTestStepRows(records: CSVRowViewModel.CSVRowViewModel[], contents: string[][], rowIndex: number, actionId: number) {
        if (records.length > 0 && !records[records.length - 1].isTestCase && (actionId % 1) == 0 && records[records.length - 1].actionId >= actionId) {
            let lineStart = rowIndex + 1;
            for (let j = rowIndex; j < contents.length; j++) {
                let row = contents[j];
                if (row[CsvFixedColumn.WorkItemType].toLowerCase() !== Resources.TestCaseText.toLowerCase()) {
                    rowIndex++;
                } else {
                    break;
                }
            }
            Diag.logError("[_skipOrphanTestStepRows] Test Steps ignored from line " + lineStart + " to " + rowIndex);
            return rowIndex;
        }
        return rowIndex;
    }
}
